import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { COMPANY_DETAILS } from "../pages/appsettings";

// Theme colors (matches app brand green #2d5a3d)
const THEME_GREEN = [45, 90, 61];
const DARK_GREEN = [26, 58, 39];

const fmt = (val) =>
  Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Formats a value that may be a plain date/datetime string OR a unix timestamp.
const formatDateTime = (val) => {
  if (!val) return "-";
  const numeric = Number(val);
  if (String(val).trim() !== "" && !isNaN(numeric)) {
    return dayjs.unix(numeric).format("YYYY/MM/DD hh:mm:ss A");
  }
  return dayjs(val).isValid() ? dayjs(val).format("YYYY/MM/DD hh:mm:ss A") : "-";
};

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/mainlogo.png";
  });

/**
 * Builds a branded, A4 Goods Received Note PDF using jsPDF + autoTable.
 * @param {Object} data - Response of grn/get.php { master, items }
 * @returns {Promise<jsPDF>} The generated jsPDF document (not yet saved)
 */
export const buildGRNPDF = async (data) => {
  const master = data?.master || {};
  const items = data?.items || [];

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // ----- Totals (recompute to guarantee correctness) -----
  let subtotal = 0;
  let totalTax = 0;
  let totalDisc = 0;
  let totalQty = 0;
  items.forEach((it) => {
    const qty = parseFloat(it.grn_qty) || 0;
    const price = parseFloat(it.cost_price) || 0;
    subtotal += qty * price;
    totalTax += parseFloat(it.tax_price) || 0;
    totalDisc += parseFloat(it.discount_price) || 0;
    totalQty += qty;
  });
  const grandTotal = subtotal + totalTax - totalDisc;

  // ----- Letterhead: logo + company details -----
  const logo = await loadLogo();
  const logoX = margin;
  const logoY = 13;
  const logoSize = 22;

  if (logo) {
    doc.addImage(logo, "PNG", logoX, logoY, logoSize, logoSize);
  } else {
    // Fallback branded box
    doc.setFillColor(...THEME_GREEN);
    doc.roundedRect(logoX, logoY, logoSize, logoSize, 2, 2, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("AF", logoX + logoSize / 2, logoY + logoSize / 2 + 1, { align: "center" });
  }

  const textX = logoX + logoSize + 7;
  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...THEME_GREEN);
  doc.text(COMPANY_DETAILS.name.toUpperCase(), textX, 18);

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(90, 90, 90);
  doc.text(COMPANY_DETAILS.address, textX, 24);
  doc.text(`Tel: ${COMPANY_DETAILS.phone} | Mobile: ${COMPANY_DETAILS.mobile}`, textX, 29);
  doc.text(`Email: ${COMPANY_DETAILS.email} | Web: ${COMPANY_DETAILS.website}`, textX, 34);

  // ----- Document title (right aligned) -----
  doc.setFontSize(17);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...THEME_GREEN);
  doc.text("GOODS RECEIVED NOTE", pageWidth - margin, 19, { align: "right" });

  doc.setFontSize(11);
  doc.setTextColor(...DARK_GREEN);
  doc.text(master.grn_ref || "", pageWidth - margin, 26, { align: "right" });

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(140, 140, 140);
  doc.text(`BR No: ${COMPANY_DETAILS.brNo}`, pageWidth - margin, 31, { align: "right" });

  // ----- Divider -----
  doc.setDrawColor(...THEME_GREEN);
  doc.setLineWidth(0.9);
  doc.line(margin, 39, pageWidth - margin, 39);

  // ----- Document info (two columns) -----
  const infoRows = [
    ["GRN Reference", master.grn_ref || "-"],
    ["Supplier", master.supplier_name || "-"],
    ["Received Date", master.grn_date ? dayjs(master.grn_date).format("YYYY-MM-DD") : "-"],
    ["Supplier Ref", master.sup_ref || "-"],
    ["Supplier Ref Date", master.sup_ref_date ? dayjs(master.sup_ref_date).format("YYYY-MM-DD") : "-"],
    ["PR Reference", master.po_ref || "-"],
    ["Status", master.grn_status || "-"],
    ["Received By", master.creator_name || "-"],
  ];

  const colWidth = (pageWidth - margin * 2) / 2;
  const half = Math.ceil(infoRows.length / 2);

  infoRows.forEach((row, i) => {
    const x = i < half ? margin : margin + colWidth;
    const y = 44.5 + (i % half) * 6.4;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 100, 100);
    doc.text(`${row[0]}:`, x, y);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(30, 30, 30);
    doc.text(String(row[1]), x + 34, y);
  });

  let y = 44.5 + half * 6.4;

  // ----- Note (PR note) -----
  if (master.pr_note) {
    y += 5;
    const noteText = String(master.pr_note);
    const noteMaxWidth = pageWidth - margin * 2 - 16;
    const lines = doc.splitTextToSize(noteText, noteMaxWidth);
    const noteHeight = 6 + lines.length * 4.2;

    doc.setFillColor(255, 251, 230);
    doc.setDrawColor(255, 229, 143);
    doc.roundedRect(margin, y, pageWidth - margin * 2, noteHeight, 1.5, 1.5, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(173, 104, 0);
    doc.text("Note:", margin + 3.5, y + 5);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 90, 20);
    doc.text(lines, margin + 14, y + 5);
    y += noteHeight + 4;
  }

  // ----- Summary bar -----
  y += 3;
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(90, 90, 90);
  doc.text(`Items: ${items.length}`, margin, y);
  doc.text(`Total Quantity: ${fmt(totalQty)}`, pageWidth / 2, y, { align: "center" });
  doc.text("Currency: Sri Lankan Rupee (LKR)", pageWidth - margin, y, { align: "right" });
  y += 4;

  // ----- Items table -----
  const bodyRows = items.map((it, idx) => {
    const qty = parseFloat(it.grn_qty) || 0;
    const price = parseFloat(it.cost_price) || 0;
    const tax = parseFloat(it.tax_price) || 0;
    const disc = parseFloat(it.discount_price) || 0;
    const lineTotal = qty * price + tax - disc;

    const desc = it.inv_desc || "-";
    const note = it.grnitem_note ? `\nNote: ${it.grnitem_note}` : "";

    return [idx + 1, it.inv_code || "-", `${desc}${note}`, fmt(qty), fmt(price), fmt(tax), fmt(disc), fmt(lineTotal)];
  });

  if (bodyRows.length === 0) {
    bodyRows.push([
      {
        content: "No items in this Goods Received Note.",
        colSpan: 8,
        styles: { halign: "center", fontStyle: "italic", textColor: [150, 150, 150] },
      },
    ]);
  }

  autoTable(doc, {
    startY: y,
    head: [["#", "Item Code", "Description", "Qty", "Unit Cost", "Tax", "Discount", "Total"]],
    body: bodyRows,
    theme: "grid",
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: THEME_GREEN,
      textColor: 255,
      fontSize: 8.5,
      halign: "center",
      cellPadding: 2.5,
    },
    styles: {
      fontSize: 8,
      cellPadding: 2.2,
      overflow: "linebreak",
      textColor: [40, 40, 40],
      lineColor: [217, 217, 217],
      lineWidth: 0.2,
    },
    columnStyles: {
      0: { cellWidth: 9, halign: "center" },
      1: { cellWidth: 24 },
      2: { cellWidth: "auto" },
      3: { cellWidth: 15, halign: "right" },
      4: { cellWidth: 22, halign: "right" },
      5: { cellWidth: 18, halign: "right" },
      6: { cellWidth: 18, halign: "right" },
      7: { cellWidth: 22, halign: "right", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index % 2 === 1) {
        data.cell.styles.fillColor = [249, 250, 251];
      }
    },
  });

  let totalsY = doc.lastAutoTable.finalY + 6;

  // ----- Totals block (right aligned) -----
  if (totalsY > pageHeight - 40) {
    doc.addPage();
    totalsY = 30;
  }

  const blockLeft = pageWidth - 88;
  const blockWidth = 74;

  const totalRows = [
    { label: "Sub Total", value: fmt(subtotal), color: [40, 40, 40] },
    { label: "Total Tax", value: fmt(totalTax), color: [40, 40, 40] },
    { label: "Total Discount", value: `(${fmt(totalDisc)})`, color: [207, 19, 34] },
  ];

  totalRows.forEach((r) => {
    doc.setFillColor(245, 245, 245);
    doc.setDrawColor(217, 217, 217);
    doc.setLineWidth(0.2);
    doc.rect(blockLeft, totalsY, blockWidth, 7.2, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(90, 90, 90);
    doc.text(r.label, blockLeft + 3, totalsY + 5);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...r.color);
    doc.text(r.value, blockLeft + blockWidth - 3, totalsY + 5, { align: "right" });

    totalsY += 7.6;
  });

  // Grand Total (theme green band)
  doc.setFillColor(...THEME_GREEN);
  doc.setDrawColor(...THEME_GREEN);
  doc.rect(blockLeft, totalsY, blockWidth, 9, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text("GRAND TOTAL", blockLeft + 3, totalsY + 6);
  doc.text(`LKR ${fmt(grandTotal)}`, blockLeft + blockWidth - 3, totalsY + 6, { align: "right" });

  totalsY += 9;

  // ----- Signatures -----
  // Reserve ~32mm from the bottom so the signature line + role label + name +
  // date/time (drawn down to sigY + ~17) never collide with the footer text.
  let sigY = Math.max(totalsY + 24, pageHeight - 42);
  if (sigY > pageHeight - 38) {
    doc.addPage();
    sigY = 40;
  }

  const sigColWidth = (pageWidth - margin * 2) / 2;
  const sigBlocks = [
    { label: "Prepared By", name: master.creator_name || "-", date: formatDateTime(master.created_dt || master.grn_date) },
    { label: "Approved By", name: master.approver_name || "-", date: formatDateTime(master.approved_dt) },
  ];
  sigBlocks.forEach((block, i) => {
    const cx = margin + sigColWidth * i + sigColWidth / 2;
    doc.setDrawColor(40, 40, 40);
    doc.setLineWidth(0.3);
    doc.line(cx - 20, sigY, cx + 20, sigY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(90, 90, 90);
    doc.text(block.label, cx, sigY + 4.5, { align: "center" });

    if (block.name && block.name !== "-") {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(30, 30, 30);
      const nameLines = doc.splitTextToSize(String(block.name), 60);
      doc.text(nameLines, cx, sigY + 9, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(120, 120, 120);
      doc.text(String(block.date || "-"), cx, sigY + 9 + nameLines.length * 4.2 + 1.5, { align: "center" });
    }
  });

  // ----- Footer -----
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(150, 150, 150);
  doc.text(
    "This is a computer-generated document. No signature is required.",
    pageWidth / 2,
    pageHeight - 9,
    { align: "center" }
  );
  doc.text(
    `Generated on ${dayjs().format("YYYY-MM-DD HH:mm:ss")} | AgriField Management System`,
    pageWidth / 2,
    pageHeight - 5.5,
    { align: "center" }
  );

  return doc;
};

/**
 * Generates and downloads a Goods Received Note PDF directly.
 */
export const downloadGRNPDF = async (data) => {
  const doc = await buildGRNPDF(data);
  const ref = data?.master?.grn_ref || "GRN";
  doc.save(`Goods_Received_Note_${ref}.pdf`);
  return doc;
};
